import type { JsonObject, ToolDef } from "@fabric-harness/sdk";
import type {
  CreateVectorIndexRequest,
  VectorSearchClient,
} from "@databricks/sdk-vectorsearch/v1";
import {
  DatabricksResourceNotAllowedError,
  assertExactResourceAllowed,
  pinnedStringResourceSchema,
  resolveExactResourcePolicy,
} from "./resource-policy.js";

export type DatabricksAiSearchAdminClient = Pick<
  VectorSearchClient,
  "createEndpoint" | "deleteEndpoint" | "getEndpoint" | "createVectorIndex"
  | "getVectorIndex" | "deleteVectorIndex" | "syncVectorIndex"
>;

/** One model-callable AI Search administration operation. */
export type DatabricksAiSearchAdminOperation =
  | "createEndpoint"
  | "deleteEndpoint"
  | "createIndex"
  | "deleteIndex"
  | "syncIndex"
  | "describeIndex";

type DatabricksAiSearchIndexArm =
  | { allowedIndexes: readonly string[]; allowAnyIndex?: never }
  | { allowedIndexes?: never; allowAnyIndex: true }
  | { allowedIndexes?: never; allowAnyIndex?: never };

type DatabricksAiSearchEndpointArm =
  | { allowedEndpoints: readonly string[]; allowAnyEndpoint?: never }
  | { allowedEndpoints?: never; allowAnyEndpoint: true }
  | { allowedEndpoints?: never; allowAnyEndpoint?: never };

/**
 * Delta-sync index creation names a serving endpoint that embeds the source column, so the column's
 * contents are sent to it. It is a distinct dimension from the Vector Search endpoint — the two must
 * never substitute for each other.
 *
 * Unlike the other dimensions, omitting both arms does not mean "dimension unused": it means
 * delta-sync creation is not granted at all. The branch is removed from the tool's schema and a
 * delta-sync spec is rejected at runtime, so a direct-vector-only agent never needs
 * `allowAnyEmbeddingModelEndpoint: true` — which would in fact grant delta-sync against any
 * embedding endpoint.
 */
type DatabricksAiSearchEmbeddingArm =
  | {
      allowedEmbeddingModelEndpoints: readonly string[];
      allowAnyEmbeddingModelEndpoint?: never;
    }
  | {
      allowedEmbeddingModelEndpoints?: never;
      allowAnyEmbeddingModelEndpoint: true;
    }
  | {
      allowedEmbeddingModelEndpoints?: never;
      allowAnyEmbeddingModelEndpoint?: never;
    };

type DatabricksAiSearchSourceTableArm = {
  /**
   * Optional exact source-table bound for delta-sync creation. When omitted, the governance
   * catalog allowlist and the executing principal's Unity Catalog grants remain authoritative.
   */
  allowedSourceTables?: readonly string[];
};

/**
 * Bounds the model-callable AI Search admin tools. `allowedOperations` decides which tools exist at
 * all; the index/endpoint arms pin the resource names those tools may name. An index or endpoint
 * dimension no registered operation uses may be omitted; omitting the embedding arm instead
 * withholds delta-sync index creation (see `DatabricksAiSearchEmbeddingArm`).
 * `{ allowAnyAiSearchAdmin: true }` is the deliberate, greppable unbounded opt-out.
 */
export type DatabricksAiSearchAdminPolicy =
  | ({
      allowedOperations: readonly DatabricksAiSearchAdminOperation[];
      allowAnyAiSearchAdmin?: never;
    } & DatabricksAiSearchIndexArm &
      DatabricksAiSearchEndpointArm &
      DatabricksAiSearchEmbeddingArm &
      DatabricksAiSearchSourceTableArm)
  | {
      allowAnyAiSearchAdmin: true;
      allowedOperations?: never;
      allowedIndexes?: never;
      allowAnyIndex?: never;
      allowedEndpoints?: never;
      allowAnyEndpoint?: never;
      allowedEmbeddingModelEndpoints?: never;
      allowAnyEmbeddingModelEndpoint?: never;
      allowedSourceTables?: never;
    };

const AI_SEARCH_ADMIN_OPERATIONS: readonly DatabricksAiSearchAdminOperation[] = [
  "createEndpoint",
  "deleteEndpoint",
  "createIndex",
  "deleteIndex",
  "syncIndex",
  "describeIndex",
];
/** Operations whose input names an index. */
const INDEX_OPERATIONS: readonly DatabricksAiSearchAdminOperation[] = [
  "createIndex",
  "deleteIndex",
  "syncIndex",
  "describeIndex",
];
/** Operations whose input names an endpoint — `createIndex` names both dimensions. */
const ENDPOINT_OPERATIONS: readonly DatabricksAiSearchAdminOperation[] = [
  "createEndpoint",
  "deleteEndpoint",
  "createIndex",
];

export type DatabricksSearchIndexSpec =
  | {
      kind: "deltaSync";
      name: string;
      endpointName: string;
      primaryKey: string;
      sourceTable: string;
      pipelineType: "TRIGGERED" | "CONTINUOUS";
      embedding: { sourceColumn: string; modelEndpoint: string };
    }
  | {
      kind: "directVector";
      name: string;
      endpointName: string;
      primaryKey: string;
      schema: Array<{ name: string; type: string }>;
      embeddingVectorColumn: { name: string; dimension: number };
    };

export class DatabricksAiSearchAdmin {
  constructor(private readonly client: DatabricksAiSearchAdminClient) {}
  createEndpoint(
    name: string,
    endpointType: "STANDARD" | "STORAGE_OPTIMIZED" = "STANDARD",
  ): Promise<unknown> {
    return this.client.createEndpoint({ name, endpointType });
  }
  deleteEndpoint(name: string): Promise<unknown> {
    return this.client.deleteEndpoint({ name });
  }
  async getEndpoint(name: string): Promise<Record<string, unknown>> {
    return plainRecord(await this.client.getEndpoint({ name }));
  }
  async waitForEndpoint(
    name: string,
    options: { timeoutMs?: number; pollIntervalMs?: number; signal?: AbortSignal } = {},
  ): Promise<Record<string, unknown>> {
    const deadline = Date.now() + (options.timeoutMs ?? 900_000);
    while (true) {
      if (options.signal?.aborted)
        throw options.signal.reason ?? new DOMException("Aborted", "AbortError");
      const endpoint = await this.getEndpoint(name);
      const state =
        (endpoint.endpointStatus as Record<string, unknown> | undefined)?.state ??
        (endpoint.status as Record<string, unknown> | undefined)?.state;
      if (state === "ONLINE") return endpoint;
      if (state === "PROVISIONING_ERROR" || state === "OFFLINE")
        throw new Error(`Databricks AI Search endpoint ${name} reached ${String(state)}.`);
      if (Date.now() >= deadline)
        throw new Error(`Databricks AI Search endpoint ${name} timed out.`);
      await sleep(jitter(options.pollIntervalMs ?? 5000), options.signal);
    }
  }
  createIndex(spec: DatabricksSearchIndexSpec): Promise<unknown> {
    return this.client.createVectorIndex(serializeIndex(spec));
  }
  async describeIndex(name: string): Promise<Record<string, unknown>> {
    return plainRecord(await this.client.getVectorIndex({ name }));
  }
  deleteIndex(name: string): Promise<unknown> {
    return this.client.deleteVectorIndex({ name });
  }
  async syncIndex(name: string): Promise<unknown> {
    const index = await this.describeIndex(name);
    const type = index.indexType;
    if (type !== "DELTA_SYNC")
      throw new Error(`Databricks AI Search index ${name} is not a delta-sync index.`);
    return this.client.syncVectorIndex({ name });
  }
}

/**
 * Model-callable AI Search administration, bounded by an explicit policy. `allowedOperations`
 * filters which tools are registered at all, and every resource name is pinned into the tool's
 * input schema and re-checked at call time before the Vector Search API is touched. The typed
 * `DatabricksAiSearchAdmin` client stays unbounded.
 */
export function databricksAiSearchAdminTools(
  client: DatabricksAiSearchAdminClient,
  policy: DatabricksAiSearchAdminPolicy,
): ToolDef[] {
  if (!policy || typeof policy !== "object" || Array.isArray(policy))
    throw new TypeError("Databricks AI Search admin policy must be an object.");
  const unbounded = (policy as { allowAnyAiSearchAdmin?: unknown }).allowAnyAiSearchAdmin === true;
  if (unbounded) assertNoBoundedArms(policy);
  const operations = unbounded
    ? new Set(AI_SEARCH_ADMIN_OPERATIONS)
    : resolveAiSearchAdminOperations(policy);
  const allowedIndexes =
    !unbounded && INDEX_OPERATIONS.some((operation) => operations.has(operation))
      ? resolveExactResourcePolicy(policy, "allowedIndexes", "allowAnyIndex", "AI Search index")
      : undefined;
  const allowedEndpoints =
    !unbounded && ENDPOINT_OPERATIONS.some((operation) => operations.has(operation))
      ? resolveExactResourcePolicy(
          policy,
          "allowedEndpoints",
          "allowAnyEndpoint",
          "AI Search endpoint",
        )
      : undefined;
  // Only delta-sync index creation names an embedding endpoint, and the branch is chosen at call
  // time. Omitting both arms is not "dimension unused": it withholds delta-sync creation entirely,
  // so a direct-vector-only agent never has to opt out with an allowAny arm that would in fact
  // grant delta-sync against any embedding endpoint.
  const embeddingArmPresent =
    (policy as Record<string, unknown>).allowedEmbeddingModelEndpoints !== undefined ||
    (policy as Record<string, unknown>).allowAnyEmbeddingModelEndpoint !== undefined;
  const deltaSyncCreation = unbounded || embeddingArmPresent;
  const sourceTableArmPresent =
    (policy as Record<string, unknown>).allowedSourceTables !== undefined;
  if (!deltaSyncCreation && sourceTableArmPresent)
    throw new TypeError(
      "Databricks AI Search allowedSourceTables requires a delta-sync embedding policy.",
    );
  const allowedEmbeddingModelEndpoints =
    !unbounded && operations.has("createIndex") && embeddingArmPresent
      ? resolveExactResourcePolicy(
          policy,
          "allowedEmbeddingModelEndpoints",
          "allowAnyEmbeddingModelEndpoint",
          "AI Search embedding model endpoint",
        )
      : undefined;
  const allowedSourceTables =
    !unbounded && operations.has("createIndex") && sourceTableArmPresent
      ? resolveExactResourcePolicy(
          policy,
          "allowedSourceTables",
          "allowAnySourceTable",
          "AI Search source table",
        )
      : undefined;
  const api = new DatabricksAiSearchAdmin(client);
  const tools: ToolDef[] = [];
  if (operations.has("createEndpoint"))
    tools.push(
      tool(
        "databricks_create_search_endpoint",
        "write",
        endpointSchema(allowedEndpoints),
        [{ kind: "endpoint", path: "/name" }],
        async (i) =>
          await api.createEndpoint(
            allowedResourceName("AI Search endpoint", i.name as string, allowedEndpoints),
            i.endpointType as never,
          ),
      ),
    );
  if (operations.has("deleteEndpoint"))
    tools.push(
      tool(
        "databricks_delete_search_endpoint",
        "write",
        nameSchema(allowedEndpoints),
        [{ kind: "endpoint", path: "/name" }],
        async (i) =>
          await api.deleteEndpoint(
            allowedResourceName("AI Search endpoint", i.name as string, allowedEndpoints),
          ),
      ),
    );
  if (operations.has("createIndex"))
    tools.push(
      tool(
        "databricks_create_search_index",
        "write",
        indexSchema(
          allowedIndexes,
          allowedEndpoints,
          allowedEmbeddingModelEndpoints,
          allowedSourceTables,
          deltaSyncCreation,
        ),
        [
          { kind: "index", path: "/name", catalogQualified: true },
          { kind: "endpoint", path: "/endpointName" },
          // Descriptors must not outlive their schema branch: a path present in zero branches is a
          // hard governance error at bundle initialization, even when marked optional.
          ...(deltaSyncCreation
            ? [
                { kind: "table", path: "/sourceTable", catalogQualified: true, optional: true },
                {
                  kind: "serving-endpoint",
                  path: "/embedding/modelEndpoint",
                  optional: true,
                },
              ]
            : []),
        ],
        async (i) => {
          const spec = i as unknown as DatabricksSearchIndexSpec;
          if (!deltaSyncCreation && spec.kind === "deltaSync")
            throw new DatabricksResourceNotAllowedError("AI Search delta-sync index creation");
          const bound = {
            name: allowedResourceName("AI Search index", spec.name, allowedIndexes),
            endpointName: allowedResourceName(
              "AI Search endpoint",
              spec.endpointName,
              allowedEndpoints,
            ),
          };
          if (spec.kind !== "deltaSync") return await api.createIndex({ ...spec, ...bound });
          return await api.createIndex({
            ...spec,
            ...bound,
            sourceTable: allowedResourceName(
              "AI Search source table",
              spec.sourceTable,
              allowedSourceTables,
            ),
            embedding: {
              ...spec.embedding,
              modelEndpoint: allowedResourceName(
                "AI Search embedding model endpoint",
                spec.embedding.modelEndpoint,
                allowedEmbeddingModelEndpoints,
              ),
            },
          });
        },
      ),
    );
  if (operations.has("deleteIndex"))
    tools.push(
      tool(
        "databricks_delete_search_index",
        "write",
        nameSchema(allowedIndexes),
        [{ kind: "index", path: "/name", catalogQualified: true }],
        async (i) =>
          await api.deleteIndex(
            allowedResourceName("AI Search index", i.name as string, allowedIndexes),
          ),
      ),
    );
  if (operations.has("syncIndex"))
    tools.push(
      tool(
        "databricks_sync_search_index",
        "execute",
        nameSchema(allowedIndexes),
        [{ kind: "index", path: "/name", catalogQualified: true }],
        async (i) =>
          await api.syncIndex(
            allowedResourceName("AI Search index", i.name as string, allowedIndexes),
          ),
      ),
    );
  if (operations.has("describeIndex"))
    tools.push(
      tool(
        "databricks_describe_search_index",
        "read",
        nameSchema(allowedIndexes),
        [{ kind: "index", path: "/name", catalogQualified: true }],
        async (i) =>
          await api.describeIndex(
            allowedResourceName("AI Search index", i.name as string, allowedIndexes),
          ),
      ),
    );
  return tools;
}

/**
 * Asserts the name is in policy and returns the canonical form to hand downstream.
 * `assertExactResourceAllowed` matches on the trimmed value, so passing the raw input to the API
 * would let `"main.kb.idx "` satisfy an allowlist entry of `"main.kb.idx"` and still reach Vector
 * Search verbatim. The allowlist is trimmed at construction, so the trimmed name is the allowed one.
 */
function allowedResourceName(
  resourceKind: string,
  value: string,
  allowed: readonly string[] | undefined,
): string {
  assertExactResourceAllowed(resourceKind, value, allowed);
  return value.trim();
}

/** Names the bounded arms are mutually exclusive with the whole-surface opt-out. */
const BOUNDED_POLICY_KEYS = [
  "allowedOperations",
  "allowedIndexes",
  "allowAnyIndex",
  "allowedEndpoints",
  "allowAnyEndpoint",
  "allowedEmbeddingModelEndpoints",
  "allowAnyEmbeddingModelEndpoint",
  "allowedSourceTables",
] as const;

/**
 * The public union already makes these arms mutually exclusive; this rejects the same contradiction
 * from untyped callers rather than silently widening to the unbounded surface, matching how
 * `resolveExactResourcePolicy` refuses a policy that sets both `allowedX` and `allowAnyX`.
 */
function assertNoBoundedArms(policy: DatabricksAiSearchAdminPolicy): void {
  const value = policy as Record<string, unknown>;
  const present = BOUNDED_POLICY_KEYS.filter((key) => value[key] !== undefined);
  if (present.length > 0)
    throw new TypeError(
      `Databricks AI Search admin policy must not combine allowAnyAiSearchAdmin: true with ${present.join(", ")}.`,
    );
}

function resolveAiSearchAdminOperations(
  policy: DatabricksAiSearchAdminPolicy,
): Set<DatabricksAiSearchAdminOperation> {
  const requested = (policy as { allowedOperations?: unknown }).allowedOperations;
  if (
    !Array.isArray(requested) ||
    requested.length === 0 ||
    !requested.every((operation) =>
      (AI_SEARCH_ADMIN_OPERATIONS as readonly unknown[]).includes(operation),
    )
  )
    throw new TypeError(
      `Databricks AI Search admin policy must set a non-empty allowedOperations (${AI_SEARCH_ADMIN_OPERATIONS.join(", ")}), or allowAnyAiSearchAdmin: true.`,
    );
  return new Set(requested as readonly DatabricksAiSearchAdminOperation[]);
}

function serializeIndex(spec: DatabricksSearchIndexSpec): CreateVectorIndexRequest {
  if (spec.kind === "deltaSync")
    return {
      name: spec.name,
      endpointName: spec.endpointName,
      primaryKey: spec.primaryKey,
      indexType: "DELTA_SYNC",
      indexSpec: { $case: "deltaSyncIndexSpec", deltaSyncIndexSpec: {
        sourceTable: spec.sourceTable,
        pipelineType: spec.pipelineType,
        embeddingSourceColumns: [
          {
            name: spec.embedding.sourceColumn,
            embeddingConfig: {
              $case: "embeddingModelEndpointName",
              embeddingModelEndpointName: spec.embedding.modelEndpoint,
            },
          },
        ],
      } },
    };
  return {
    name: spec.name,
    endpointName: spec.endpointName,
    primaryKey: spec.primaryKey,
    indexType: "DIRECT_ACCESS",
    indexSpec: { $case: "directAccessIndexSpec", directAccessIndexSpec: {
      schemaJson: JSON.stringify(
        Object.fromEntries(spec.schema.map((column) => [column.name, column.type])),
      ),
      embeddingVectorColumns: [
        {
          name: spec.embeddingVectorColumn.name,
          embeddingDimension: spec.embeddingVectorColumn.dimension,
        },
      ],
    } },
  };
}

function plainRecord(value: unknown): Record<string, unknown> {
  return JSON.parse(JSON.stringify(value, (_, item) => typeof item === "bigint" ? Number(item) : item)) as Record<string, unknown>;
}
function tool(
  name: string,
  effect: "read" | "write" | "execute",
  inputSchema: Record<string, unknown>,
  resources: JsonObject[],
  execute: (input: JsonObject) => unknown,
): ToolDef {
  return {
    name,
    description: name.replaceAll("_", " "),
    inputSchema,
    metadata: { effect, provider: "databricks", service: "ai-search", governance: { resources } },
    execute,
  };
}
/** `name` is the endpoint for the endpoint tools and the index for the index tools. */
function nameSchema(allowed: readonly string[] | undefined): Record<string, unknown> {
  return {
    type: "object",
    additionalProperties: false,
    properties: { name: pinnedStringResourceSchema(allowed) },
    required: ["name"],
  };
}
function endpointSchema(allowedEndpoints: readonly string[] | undefined): Record<string, unknown> {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      name: pinnedStringResourceSchema(allowedEndpoints),
      endpointType: { type: "string", enum: ["STANDARD", "STORAGE_OPTIMIZED"] },
    },
    required: ["name"],
  };
}
function indexSchema(
  allowedIndexes: readonly string[] | undefined,
  allowedEndpoints: readonly string[] | undefined,
  allowedEmbeddingModelEndpoints: readonly string[] | undefined,
  allowedSourceTables: readonly string[] | undefined,
  deltaSyncCreation: boolean,
): Record<string, unknown> {
  return {
    // The root type is required: normalizeOpenAIParameters replaces any schema whose root is not
    // an object with an empty one, which would drop `oneOf` and the pins inside it.
    type: "object",
    oneOf: [
      // Withheld delta-sync creation is removed from the schema, not merely rejected at runtime:
      // the model is never shown a branch it cannot use.
      ...(deltaSyncCreation
        ? [
            {
              type: "object",
              additionalProperties: false,
              properties: {
                kind: { const: "deltaSync" },
                name: pinnedStringResourceSchema(allowedIndexes),
                endpointName: pinnedStringResourceSchema(allowedEndpoints),
                primaryKey: { type: "string" },
                sourceTable: pinnedStringResourceSchema(allowedSourceTables),
                pipelineType: { type: "string", enum: ["TRIGGERED", "CONTINUOUS"] },
                embedding: {
                  type: "object",
                  properties: {
                    sourceColumn: { type: "string" },
                    modelEndpoint: pinnedStringResourceSchema(allowedEmbeddingModelEndpoints),
                  },
                  required: ["sourceColumn", "modelEndpoint"],
                },
              },
              required: [
                "kind",
                "name",
                "endpointName",
                "primaryKey",
                "sourceTable",
                "pipelineType",
                "embedding",
              ],
            },
          ]
        : []),
      {
        type: "object",
        additionalProperties: false,
        properties: {
          kind: { const: "directVector" },
          name: pinnedStringResourceSchema(allowedIndexes),
          endpointName: pinnedStringResourceSchema(allowedEndpoints),
          primaryKey: { type: "string" },
          schema: {
            type: "array",
            items: {
              type: "object",
              properties: { name: { type: "string" }, type: { type: "string" } },
              required: ["name", "type"],
            },
          },
          embeddingVectorColumn: {
            type: "object",
            properties: { name: { type: "string" }, dimension: { type: "number" } },
            required: ["name", "dimension"],
          },
        },
        required: ["kind", "name", "endpointName", "primaryKey", "schema", "embeddingVectorColumn"],
      },
    ],
  };
}
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(signal.reason);
      },
      { once: true },
    );
  });
}
function jitter(ms: number): number {
  return Math.max(1, Math.round(ms * (0.9 + Math.random() * 0.2)));
}
